import { createInstance } from '@/helpers/api/base'
import { useGetUserInfo } from '@/modules/Auth'
import { useGetReference } from '@/modules/CombinedLogs'
import { useGetHubs } from '@/modules/Workstation/api/useGetHubs'
import { DailyLogCode, RouteSelectionMode } from '@/types/logs'
import { IUserShift } from '@/types/user'
import { useQueryClient } from '@tanstack/react-query'
import { useAppMutation } from '@/helpers/api/mutation'
import { formatInTimeZone } from 'date-fns-tz'
import { useMemo } from 'react'

interface ICreateControllerLogRequest {
    id: string
    reference?: string
    code?: string
    routes?: string[]
    description?: string
    routeSelectionMode?: RouteSelectionMode
    author?: string
    timeStamp: string | null
    workstation?: string
    report: null
    mechanicalInfo: null
    source: string
}

interface ICreateControllerLogResponse {
    id: string
}
export const useCreateAutomaticStartShiftLog = () => {
    const queryClient = useQueryClient()
    const { data: reference } = useGetReference()
    const { data: userInfo } = useGetUserInfo()
    const { data: hubs } = useGetHubs()
    const allWorkstations = useMemo(() => (hubs ? hubs.flatMap((item) => item.workstations) : []), [hubs])

    const createControllerLogMutation = useAppMutation<
        ICreateControllerLogResponse,
        Error,
        ICreateControllerLogRequest
    >({
        mutationFn: (data) =>
            createInstance({
                url: '/controller-logs',
                method: 'POST',
                data,
            }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['controllerLogs'] })
            await queryClient.invalidateQueries({ queryKey: ['reference'] })
        },
    })

    const handleCreateAutomaticStartShiftLog = (shiftData: IUserShift, workstationCode: string) => {
        const workstation = allWorkstations.find((workstation) => workstation.workstationNumber === workstationCode)
        if (!workstation?.workstationNumber || !userInfo) return

        const userName = userInfo.userDetails.name
        const dutyReference = shiftData.dutyCode
        const shiftStart = new Date(shiftData.dutyFrom).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        const shiftEnd = new Date(shiftData.dutyTo).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        const description = `${userName} logging in for Duty: ${dutyReference}, ${shiftStart} - ${shiftEnd}`

        createControllerLogMutation.mutate({
            id: crypto.randomUUID(),
            reference,
            code: DailyLogCode.Info,
            routes: workstation.routes.map((route) => route.route),
            routeSelectionMode: RouteSelectionMode.All,
            description,
            author: userInfo.lastSessionDetails.authorName,
            timeStamp: formatInTimeZone(new Date(), 'UTC', "yyyy-MM-dd'T'HH:mm:ss"),
            workstation: workstation.workstationNumber,
            source: 'Automatic',
            report: null,
            mechanicalInfo: null,
        })
    }

    return { handleCreateAutomaticStartShiftLog }
}
